/**
 * WorkflowSetupWizard
 *
 * A guided, plain-language wizard that produces a correctly configured
 * Workflow + (optionally) a CycleTemplate without requiring users to
 * understand the underlying configuration model.
 *
 * Accessible at: /prioritization/settings/workflows/guided
 * Entry point: "Guided setup" button on the Workflows settings page.
 *
 * The wizard does NOT create a cycle — it produces a reusable Workflow
 * and Template that can be selected when creating any future cycle.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Loader2,
  Sparkles,
  Users,
  Timer,
  Shield,
  Layers,
  BarChart3,
  Sliders,
  Zap,
  Lock,
  Unlock,
  RefreshCw,
  GitBranch,
  LayoutTemplate,
  Plus,
  X,
} from 'lucide-react';
import { useWorkflowStore } from '../../../stores/prioritization/workflowStore';
import { useCycleTemplateStore } from '../../../stores/prioritization/cycleTemplateStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { WorkflowConfiguration } from '../../../types/prioritization/workflow';
import type { TemplateAttributePreset, TemplateRangePreset } from '../../../types/prioritization/cycleTemplate';
import {
  wizardBtnPrimary,
  wizardBtnSecondary,
  wizardCard,
  wizardCardPad,
  wizardInput,
  wizardSectionTitle,
  wizardSectionDesc,
  wizardShell,
  wizardHeaderBar,
} from '../../../components/prioritization/cycle-setup/setupWizardTheme';

// ─────────────────────────────────────────────────────────────────────────────
// Wizard state
// ─────────────────────────────────────────────────────────────────────────────

type Intent = 'periodic' | 'light' | 'executive' | 'workshop' | 'distributed' | 'custom';
type SessionFormat = 'facilitator' | 'participant' | 'async' | 'flexible';
type CriteriaControl = 'workflow_defined' | 'cycle_defined';
type ScaleType = 'fibonacci' | 'tshirt' | 'number_1_5' | 'number_1_10';

interface DraftCriterion {
  key: string;
  label: string;
  scaleType: ScaleType;
  required: boolean;
}

interface WizardState {
  // Step 1
  name: string;
  intent: Intent | null;
  // Step 2
  sessionFormat: SessionFormat | null;
  allowTeamOverride: boolean;
  // Step 3
  approvalRequired: boolean;
  maxLoops: number | null; // null = unlimited
  // Step 4
  criteriaControl: CriteriaControl | null;
  lockRanges: boolean;
  // Step 5
  attributeWriteBack: 'direct' | 'session_only' | 'none';
  resultWriteBack: 'to_track' | 'none';
  resultResolution: 'latest_published' | 'aggregate' | 'selected_official';
  // Step 6
  defaultCriteria: DraftCriterion[];
}

const INITIAL: WizardState = {
  name: '',
  intent: null,
  sessionFormat: null,
  allowTeamOverride: true,
  approvalRequired: true,
  maxLoops: null,
  criteriaControl: null,
  lockRanges: false,
  attributeWriteBack: 'direct',
  resultWriteBack: 'to_track',
  resultResolution: 'latest_published',
  defaultCriteria: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// Option card
// ─────────────────────────────────────────────────────────────────────────────

function OptionCard<T extends string>({
  value,
  current,
  icon: Icon,
  title,
  subtitle,
  onClick,
}: {
  value: T;
  current: T | null;
  icon: React.ElementType;
  title: string;
  subtitle: string;
  onClick: (v: T) => void;
}) {
  const selected = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={[
        'group relative w-full text-left  border-2 p-4 transition-all focus:outline-none focus:ring-2 focus:ring-primary-400',
        selected
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30 dark:border-primary-500'
          : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-primary-300 hover:bg-primary-50/40 dark:hover:border-primary-700 dark:hover:bg-primary-950/15',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <div className={[
          'mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center ',
          selected
            ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-300'
            : 'bg-[var(--hover-tint)] dark:bg-[var(--surface)] text-[var(--text-ter)] dark:text-[var(--text-sec)]',
        ].join(' ')}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={[
            'text-sm font-semibold',
            selected ? 'text-primary-800 dark:text-primary-200' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]',
          ].join(' ')}>{title}</p>
          <p className={[
            'mt-0.5 text-xs leading-relaxed',
            selected ? 'text-primary-600 dark:text-primary-300' : 'text-[var(--text-ter)] dark:text-[var(--text-sec)]',
          ].join(' ')}>{subtitle}</p>
        </div>
        {selected && (
          <CheckCircle2 size={18} className="flex-shrink-0 text-primary-500 dark:text-primary-400" />
        )}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Toggle row
// ─────────────────────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3 border border-[var(--rule)] dark:border-[var(--rule)] p-4">
      <div className="flex-1">
        <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{label}</p>
        {hint && <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">{hint}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={[
          'relative mt-0.5 h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400',
          value ? 'bg-primary-500' : 'bg-[var(--rule-bright)] dark:bg-[var(--rule-bright)]',
        ].join(' ')}
      >
        <span className={[
          'absolute top-0.5 h-4 w-4 rounded-full bg-[var(--bg-raised)] shadow transition-transform',
          value ? 'translate-x-4' : 'translate-x-0.5',
        ].join(' ')} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step components
// ─────────────────────────────────────────────────────────────────────────────

function StepName({ state, update }: { state: WizardState; update: (patch: Partial<WizardState>) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <label className={`block mb-1 ${wizardSectionTitle}`}>What should we call this workflow?</label>
        <p className={wizardSectionDesc}>This name appears when people choose a workflow for a cycle.</p>
        <input
          className={`mt-2 ${wizardInput}`}
          placeholder="e.g. Product Team Quarterly Prioritization"
          value={state.name}
          onChange={e => update({ name: e.target.value })}
          autoFocus
        />
      </div>

      <div>
        <p className={`mb-2 ${wizardSectionTitle}`}>What's this primarily for?</p>
        <p className={`mb-3 ${wizardSectionDesc}`}>This shapes the default settings — you can adjust everything on the next screens.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <OptionCard value="periodic" current={state.intent} icon={RefreshCw} onClick={v => update({ intent: v })}
            title="Regular prioritization cycles" subtitle="Structured, recurring process with full governance and review rounds" />
          <OptionCard value="light" current={state.intent} icon={Zap} onClick={v => update({ intent: v })}
            title="Quick team decisions" subtitle="Fast, lightweight sessions with minimal overhead — results are informational" />
          <OptionCard value="executive" current={state.intent} icon={Shield} onClick={v => update({ intent: v })}
            title="Executive or leadership decisions" subtitle="A decision authority picks the result that counts after a bounded review process" />
          <OptionCard value="workshop" current={state.intent} icon={Users} onClick={v => update({ intent: v })}
            title="Workshop or offsite" subtitle="One-time sessions outside the system — items can be imported back afterward" />
          <OptionCard value="distributed" current={state.intent} icon={BarChart3} onClick={v => update({ intent: v })}
            title="Broad input collection" subtitle="Many people score independently and asynchronously — results are aggregated" />
          <OptionCard value="custom" current={state.intent} icon={Sliders} onClick={v => update({ intent: v })}
            title="Something custom" subtitle="I'll configure each setting myself" />
        </div>
      </div>
    </div>
  );
}

function StepSession({ state, update }: { state: WizardState; update: (patch: Partial<WizardState>) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>How will people typically score items?</p>
        <p className={`mb-3 ${wizardSectionDesc}`}>This becomes the default for all sessions in cycles using this workflow.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <OptionCard value="facilitator" current={state.sessionFormat} icon={Users} onClick={v => update({ sessionFormat: v })}
            title="A facilitator leads the group" subtitle="One person drives the session — great for structured, deliberate scoring" />
          <OptionCard value="participant" current={state.sessionFormat} icon={GitBranch} onClick={v => update({ sessionFormat: v })}
            title="The group scores together" subtitle="Participants score in real time without a designated driver" />
          <OptionCard value="async" current={state.sessionFormat} icon={Timer} onClick={v => update({ sessionFormat: v })}
            title="People score on their own time" subtitle="Async — no live session required. Good for distributed teams or large groups" />
          <OptionCard value="flexible" current={state.sessionFormat} icon={Sliders} onClick={v => update({ sessionFormat: v })}
            title="It depends on the cycle" subtitle="Allow each cycle to choose its own session format" />
        </div>
      </div>

      <div className="space-y-2">
        <p className={`${wizardSectionTitle}`}>Flexibility</p>
        <ToggleRow
          label="Allow teams to customize the session format for their own cycle"
          hint="When off, every cycle using this workflow must use the format you set above"
          value={state.allowTeamOverride}
          onChange={v => update({ allowTeamOverride: v })}
        />
      </div>
    </div>
  );
}

function StepGovernance({ state, update }: { state: WizardState; update: (patch: Partial<WizardState>) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>After sessions finish and results come in, what happens?</p>
        <p className={`mb-3 ${wizardSectionDesc}`}>This controls whether results are immediately final or go through a review/approval process.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <OptionCard value={false as unknown as string} current={String(state.approvalRequired)} icon={Zap}
            onClick={() => update({ approvalRequired: false, maxLoops: null })}
            title="Results are immediately final" subtitle="No approval step — whatever the sessions produce is the answer" />
          <OptionCard value={true as unknown as string} current={String(state.approvalRequired)} icon={Shield}
            onClick={() => update({ approvalRequired: true })}
            title="Results go through a review/approval process" subtitle="Rankings are proposals first — they can be challenged, adjusted, or approved before they go on record" />
        </div>
      </div>

      {state.approvalRequired && (
        <div className="space-y-3 border border-primary-200 dark:border-primary-800/50 bg-primary-50 dark:bg-primary-950/20 p-4">
          <p className={wizardSectionTitle}>Review process details</p>
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Maximum number of review rounds
            </label>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-2">
              Prevents the process from going in circles. Leave blank to allow as many rounds as needed.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="1"
                max="10"
                placeholder="Unlimited"
                value={state.maxLoops ?? ''}
                onChange={e => update({ maxLoops: e.target.value ? parseInt(e.target.value) : null })}
                className={`w-32 ${wizardInput}`}
              />
              {state.maxLoops && (
                <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                  round{state.maxLoops !== 1 ? 's' : ''} max
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StepCriteria({ state, update }: { state: WizardState; update: (patch: Partial<WizardState>) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>Who decides what criteria items are evaluated on?</p>
        <p className={`mb-3 ${wizardSectionDesc}`}>
          Criteria are things like "Strategic value," "Effort," or "Customer impact" — the dimensions used to compare items.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          <OptionCard value="workflow_defined" current={state.criteriaControl} icon={Lock} onClick={v => update({ criteriaControl: v })}
            title="This workflow sets the criteria" subtitle="Cycles can't add or remove criteria. Ensures consistent measurement across all cycles using this workflow" />
          <OptionCard value="cycle_defined" current={state.criteriaControl} icon={Unlock} onClick={v => update({ criteriaControl: v, lockRanges: false })}
            title="Each cycle defines its own criteria" subtitle="Teams or cycle designers choose what to measure. More flexibility, less consistency" />
        </div>
      </div>

      {state.criteriaControl === 'workflow_defined' && (
        <div className="space-y-2">
          <ToggleRow
            label="Also standardize the scoring scales"
            hint="When on, all cycles must use the exact same scoring ranges (e.g. the same Fibonacci sequence or 1–5 scale). Prevents cross-cycle range drift."
            value={state.lockRanges}
            onChange={v => update({ lockRanges: v })}
          />
        </div>
      )}

      <div className={`${wizardCard} p-4`}>
        <div className="flex items-start gap-2 text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
          <Layers size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            <strong>Tip:</strong> Either way, you can define default criteria on the next screen —
            they'll be seeded into every new cycle automatically. The difference is whether teams
            can change them after the cycle is created.
          </span>
        </div>
      </div>
    </div>
  );
}

function StepScoreHandling({ state, update }: { state: WizardState; update: (patch: Partial<WizardState>) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>Should session scoring update item records in the system?</p>
        <p className={`mb-3 ${wizardSectionDesc}`}>
          "Item records" means the attributes stored on each priority item — things like the Impact or Effort scores.
        </p>
        <div className="grid gap-2">
          <OptionCard value="direct" current={state.attributeWriteBack} icon={BarChart3} onClick={v => update({ attributeWriteBack: v })}
            title="Yes — offer them up as values" subtitle="When a session closes, each item's scores are offered as contributions. Someone who approves values accepts them as official; the official value never changes on its own." />
          <OptionCard value="session_only" current={state.attributeWriteBack} icon={Timer} onClick={v => update({ attributeWriteBack: v })}
            title="Only within the session" subtitle="When a session closes, each item's scores are saved as that session's values. The official values don't change." />
          <OptionCard value="none" current={state.attributeWriteBack} icon={X} onClick={v => update({ attributeWriteBack: v })}
            title="No — don't save scores as values" subtitle="Pure scoring exercise with no write-back at all" />
        </div>
      </div>

      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>How should final results be combined when multiple sessions exist?</p>
        <p className={`mb-3 ${wizardSectionDesc}`}>When there's more than one session on a track, this controls which result counts.</p>
        <div className="grid gap-2">
          <OptionCard value="latest_published" current={state.resultResolution} icon={RefreshCw} onClick={v => update({ resultResolution: v })}
            title="Use the most recent published ranking" subtitle="The latest accepted result wins. Good for iterative refinement." />
          <OptionCard value="selected_official" current={state.resultResolution} icon={Shield} onClick={v => update({ resultResolution: v })}
            title="A decision authority picks the one that counts" subtitle="Someone chooses which session's published result counts for the track. Best for executive decisions." />
        </div>
        {/* S-22 (owner ruling 2026-10-14): combining results is later. */}
        <p className={`mt-2 ${wizardSectionDesc}`}>Combining results from several sessions is not available yet.</p>
      </div>
    </div>
  );
}

const SCALE_OPTIONS: { value: ScaleType; label: string; preview: string }[] = [
  { value: 'fibonacci', label: 'Fibonacci', preview: '1, 2, 3, 5, 8, 13, 21' },
  { value: 'tshirt', label: 'T-shirt sizes', preview: 'XS, S, M, L, XL, XXL' },
  { value: 'number_1_5', label: 'Numbers 1–5', preview: '1, 2, 3, 4, 5' },
  { value: 'number_1_10', label: 'Numbers 1–10', preview: '1 – 10' },
];

function StepDefaultCriteria({
  state,
  update,
}: {
  state: WizardState;
  update: (patch: Partial<WizardState>) => void;
}) {
  const attributes = useAttributesStore(s => s.attributes) ?? [];
  const [showCustom, setShowCustom] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newScale, setNewScale] = useState<ScaleType>('fibonacci');

  const usedKeys = new Set(state.defaultCriteria.map(c => c.key));
  const available = attributes.filter(a =>
    !usedKeys.has(a.key) && (!a.isCustomField || a.usable_as_criterion === true)
  );

  function addFromWorkspace(key: string, label: string) {
    update({
      defaultCriteria: [
        ...state.defaultCriteria,
        { key, label, scaleType: 'fibonacci', required: true },
      ],
    });
  }

  function addCustom() {
    const label = newLabel.trim();
    if (!label) return;
    const key = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (usedKeys.has(key)) return;
    update({
      defaultCriteria: [
        ...state.defaultCriteria,
        { key, label, scaleType: newScale, required: true },
      ],
    });
    setNewLabel('');
    setShowCustom(false);
  }

  function removeCriterion(key: string) {
    update({ defaultCriteria: state.defaultCriteria.filter(c => c.key !== key) });
  }

  function updateCriterion(key: string, patch: Partial<DraftCriterion>) {
    update({
      defaultCriteria: state.defaultCriteria.map(c => c.key === key ? { ...c, ...patch } : c),
    });
  }

  const isWorkflowLocked = state.criteriaControl === 'workflow_defined';

  return (
    <div className="space-y-5">
      <div>
        <p className={`mb-1 ${wizardSectionTitle}`}>Default criteria</p>
        <p className={wizardSectionDesc}>
          {isWorkflowLocked
            ? 'These criteria are seeded into every cycle and locked — teams cannot remove or swap them.'
            : 'These criteria are seeded into every cycle as a starting point. Teams can add or change them.'}
        </p>
      </div>

      {/* Selected criteria */}
      {state.defaultCriteria.length > 0 && (
        <div className="space-y-2">
          {state.defaultCriteria.map(c => (
            <div key={c.key} className="flex items-center gap-2 border border-primary-200 dark:border-primary-800/60 bg-primary-50 dark:bg-primary-950/20 px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{c.label}</p>
              </div>
              <select
                value={c.scaleType}
                onChange={e => updateCriterion(c.key, { scaleType: e.target.value as ScaleType })}
                className="text-xs border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] px-2 py-1"
              >
                {SCALE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {!isWorkflowLocked && (
                <label className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                  <input
                    type="checkbox"
                    checked={c.required}
                    onChange={e => updateCriterion(c.key, { required: e.target.checked })}
                    className="rounded"
                  />
                  Required
                </label>
              )}
              <button
                type="button"
                onClick={() => removeCriterion(c.key)}
                className="text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pick from workspace attributes — primary action */}
      {available.length > 0 && (
        <div>
          <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">
            {state.defaultCriteria.length === 0
              ? 'Pick criteria from your workspace:'
              : 'Add more from your workspace:'}
          </p>
          <div className="flex flex-wrap gap-2">
            {available.map(a => (
              <button
                key={a.key}
                type="button"
                onClick={() => addFromWorkspace(a.key, a.label)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:border-primary-400 hover:bg-primary-50 dark:hover:border-primary-600 dark:hover:bg-primary-950/20 transition-colors"
              >
                <Plus size={13} className="text-[var(--text-ter)] group-hover:text-primary-500" />
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {available.length === 0 && state.defaultCriteria.length === 0 && (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic text-center py-2">
          No attributes defined in your workspace yet.
        </p>
      )}

      {/* Custom criterion — secondary / text link */}
      <div className="pt-1 border-t border-[var(--rule)] dark:border-[var(--rule)]">
        {!showCustom ? (
          <button
            type="button"
            onClick={() => setShowCustom(true)}
            className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
          >
            + Add a custom criterion not in your workspace
          </button>
        ) : (
          <div className="flex items-end gap-2 pt-1">
            <div className="flex-1">
              <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                Custom criterion name
              </label>
              <input
                autoFocus
                className={wizardInput}
                placeholder="e.g. Customer impact"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCustom(); if (e.key === 'Escape') setShowCustom(false); }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Scale</label>
              <select
                value={newScale}
                onChange={e => setNewScale(e.target.value as ScaleType)}
                className="h-9 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-sm px-2 text-[var(--text-sec)] dark:text-[var(--text-sec)]"
              >
                {SCALE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={!newLabel.trim()}
              onClick={addCustom}
              className={`${wizardBtnSecondary} disabled:opacity-40`}
            >
              <Plus size={14} /> Add
            </button>
            <button type="button" onClick={() => setShowCustom(false)} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] pb-1">
              <X size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Review step
// ─────────────────────────────────────────────────────────────────────────────

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{label}</span>
      <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] text-right">{value}</span>
    </div>
  );
}

function StepReview({ state }: { state: WizardState }) {
  const sessionLabels: Record<string, string> = {
    facilitator: 'Facilitator-led (live)',
    participant: 'Group scoring (live)',
    async: 'Async / self-paced',
    flexible: 'Flexible — cycle decides',
  };
  const writeBackLabels: Record<string, string> = {
    direct: 'Yes — offer them up as values',
    session_only: "Saved as the session's values",
    none: 'No write-back',
  };
  const resolutionLabels: Record<string, string> = {
    latest_published: 'Most recent published result',
    aggregate: 'Aggregate across sessions',
    selected_official: 'Decision authority selects',
  };

  const willCreateTemplate = state.defaultCriteria.length > 0;

  return (
    <div className="space-y-5">
      <div className={`${wizardCardPad}`}>
        <div className="flex items-center gap-2 mb-3">
          <GitBranch size={15} className="text-primary-500" />
          <p className={wizardSectionTitle}>Workflow: <span className="text-primary-700 dark:text-primary-300">{state.name || '(unnamed)'}</span></p>
        </div>
        <ReviewRow label="Session format" value={sessionLabels[state.sessionFormat ?? ''] ?? '—'} />
        <ReviewRow label="Teams can customize sessions" value={state.allowTeamOverride ? 'Yes' : 'No'} />
        <ReviewRow label="Review/approval process" value={state.approvalRequired ? `Yes${state.maxLoops ? ` (max ${state.maxLoops} rounds)` : ' (unlimited rounds)'}` : 'No — results are immediately final'} />
        <ReviewRow label="Criteria control" value={state.criteriaControl === 'workflow_defined' ? 'Workflow-defined (locked)' : 'Each cycle defines its own'} />
        {state.criteriaControl === 'workflow_defined' && (
          <ReviewRow label="Scoring scales standardized" value={state.lockRanges ? 'Yes — all cycles use same scales' : 'No — scales can vary per cycle'} />
        )}
        <ReviewRow label="Score write-back" value={writeBackLabels[state.attributeWriteBack]} />
        <ReviewRow label="Results combined as" value={resolutionLabels[state.resultResolution]} />
      </div>

      {willCreateTemplate ? (
        <div className={`${wizardCardPad}`}>
          <div className="flex items-center gap-2 mb-3">
            <LayoutTemplate size={15} className="text-[var(--green)]" />
            <p className={wizardSectionTitle}>Template: <span className="text-[var(--green)] dark:text-[var(--text-ter)]">{state.name || '(unnamed)'} — Defaults</span></p>
          </div>
          <p className={`${wizardSectionDesc} mb-3`}>Applied once when a cycle is created — seeds criteria into every new cycle.</p>
          {state.defaultCriteria.map(c => (
            <div key={c.key} className="flex items-center justify-between py-1.5 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
              <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">{c.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  {SCALE_OPTIONS.find(o => o.value === c.scaleType)?.preview}
                </span>
                {c.required && (
                  <span className="text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 px-1.5 py-0.5 rounded-full">required</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`${wizardCard} p-4`}>
          <div className="flex items-center gap-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            <LayoutTemplate size={13} />
            No default criteria — no template will be created. Cycles start blank.
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Config builder
// ─────────────────────────────────────────────────────────────────────────────

function buildRangePreset(scaleType: ScaleType): TemplateRangePreset {
  switch (scaleType) {
    case 'fibonacci': return { type: 'fibonacci', options: [1, 2, 3, 5, 8, 13, 21] };
    case 'tshirt': return { type: 'tshirt', options: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] };
    case 'number_1_5': return { type: 'number', min: 1, max: 5, step: 1 };
    case 'number_1_10': return { type: 'number', min: 1, max: 10, step: 1 };
  }
}

function buildWorkflowConfig(state: WizardState): WorkflowConfiguration {
  const sessionModeMap: Record<SessionFormat, string[]> = {
    facilitator: ['cycle_linked'],
    participant: ['cycle_linked'],
    async: ['cycle_linked'],
    flexible: ['cycle_linked', 'app_linked', 'isolated'],
  };
  const runModeMap: Record<SessionFormat, string> = {
    facilitator: 'facilitator_live',
    participant: 'participant_live',
    async: 'open',
    flexible: 'facilitator_live',
  };

  const lockedAttributes = state.criteriaControl === 'workflow_defined'
    ? state.defaultCriteria.map(c => ({
        attributeKey: c.key,
        required: true,
        lockedRange: state.lockRanges ? buildRangePreset(c.scaleType) : null,
      }))
    : [];

  return {
    allowed_session_modes: (sessionModeMap[state.sessionFormat ?? 'flexible'] ?? ['cycle_linked', 'app_linked', 'isolated']) as WorkflowConfiguration['allowed_session_modes'],
    default_run_mode: (runModeMap[state.sessionFormat ?? 'flexible'] ?? null) as WorkflowConfiguration['default_run_mode'],
    attribute_write_back: state.attributeWriteBack,
    result_write_back: state.resultWriteBack,
    governance_write_back: (state.approvalRequired ? 'proposal_eligible' : 'none') as WorkflowConfiguration['governance_write_back'],
    result_resolution: state.resultResolution as WorkflowConfiguration['result_resolution'],
    allow_track_resolution_override: state.resultResolution === 'aggregate',
    catchball_enabled: state.approvalRequired,
    // Stored via index signature — runtime governance fields
    max_loops: state.maxLoops ?? null,
    criteria_governance: {
      mode: state.criteriaControl ?? 'cycle_defined',
      lockedAttributes,
    },
  };
}

function buildTemplateAttributes(state: WizardState): TemplateAttributePreset[] {
  return state.defaultCriteria.map(c => ({
    attributeKey: c.key,
    required: c.required,
    defaultRange: buildRangePreset(c.scaleType),
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Steps config
// ─────────────────────────────────────────────────────────────────────────────

interface Step {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  isComplete: (s: WizardState) => boolean;
}

const STEPS: Step[] = [
  {
    id: 'name', title: 'Name & purpose', subtitle: 'What is this workflow for?',
    icon: Sparkles, isComplete: s => !!s.name.trim() && !!s.intent,
  },
  {
    id: 'session', title: 'Session format', subtitle: 'How will scoring happen?',
    icon: Users, isComplete: s => !!s.sessionFormat,
  },
  {
    id: 'governance', title: 'Review & approval', subtitle: 'What happens after sessions?',
    icon: Shield, isComplete: () => true,
  },
  {
    id: 'criteria', title: 'Criteria control', subtitle: 'Who owns the evaluation criteria?',
    icon: Lock, isComplete: s => !!s.criteriaControl,
  },
  {
    id: 'scores', title: 'Score handling', subtitle: 'Where do results go?',
    icon: BarChart3, isComplete: () => true,
  },
  {
    id: 'defaults', title: 'Default criteria', subtitle: 'Seed criteria into new cycles',
    icon: LayoutTemplate, isComplete: () => true,
  },
  {
    id: 'review', title: 'Review & create', subtitle: 'Confirm and create',
    icon: Check, isComplete: () => true,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Main wizard
// ─────────────────────────────────────────────────────────────────────────────

export default function WorkflowSetupWizard() {
  const [step, setStep] = useState(0);
  const [state, setState] = useState<WizardState>(INITIAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const createWorkflow = useWorkflowStore(s => s.createWorkflow);
  const createTemplate = useCycleTemplateStore(s => s.createTemplate);
  const userId = useUserStore(s => s.userId) ?? '';

  function update(patch: Partial<WizardState>) {
    setState(prev => ({ ...prev, ...patch }));
  }

  const currentStep = STEPS[step];
  const canAdvance = currentStep.isComplete(state);
  const isLast = step === STEPS.length - 1;

  function applyIntentPresets(intent: Intent, _current: WizardState): Partial<WizardState> {
    const presets: Record<Intent, Partial<WizardState>> = {
      periodic: { sessionFormat: 'facilitator', approvalRequired: true, maxLoops: null, criteriaControl: 'cycle_defined', attributeWriteBack: 'direct', resultWriteBack: 'to_track', resultResolution: 'latest_published' },
      light: { sessionFormat: 'flexible', approvalRequired: false, criteriaControl: 'cycle_defined', attributeWriteBack: 'direct', resultWriteBack: 'to_track', resultResolution: 'latest_published' },
      executive: { sessionFormat: 'facilitator', approvalRequired: true, maxLoops: 3, criteriaControl: 'cycle_defined', attributeWriteBack: 'direct', resultWriteBack: 'to_track', resultResolution: 'selected_official' },
      workshop: { sessionFormat: 'flexible', approvalRequired: false, criteriaControl: 'cycle_defined', attributeWriteBack: 'none', resultWriteBack: 'none', resultResolution: 'latest_published' },
      distributed: { sessionFormat: 'async', approvalRequired: true, maxLoops: null, criteriaControl: 'workflow_defined', attributeWriteBack: 'direct', resultWriteBack: 'to_track', resultResolution: 'latest_published' },
      custom: {},
    };
    return { ...presets[intent], intent };
  }

  const enhancedUpdate = useMemo(() => {
    return (patch: Partial<WizardState>) => {
      if (patch.intent) {
        setState(prev => ({ ...prev, ...applyIntentPresets(patch.intent!, prev) }));
      } else {
        setState(prev => ({ ...prev, ...patch }));
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateWorkflow = useWorkflowStore(s => s.updateWorkflow);

  async function handleCreate() {
    setSaving(true);
    setError(null);
    try {
      const config = buildWorkflowConfig(state);
      const templateAttrs = buildTemplateAttributes(state);
      const hasTemplate = templateAttrs.length > 0;

      // 1. Create template (seeded criteria)
      let templateId: string | null = null;
      if (hasTemplate) {
        const tmpl = await createTemplate({
          name: `${state.name} — Defaults`,
          description: `Default criteria for "${state.name}" workflow. Applied once at cycle creation.`,
          defaultAttributes: templateAttrs,
          defaultParticipants: [],
          defaultGroups: [],
          createdBy: userId,
        });
        templateId = tmpl?.id ?? null;
      }

      // 2. Create workflow shell
      const wf = await createWorkflow({
        name: state.name,
        description: '',
        status: 'active',
        createdBy: userId,
      });

      // 3. Update with full config + template link
      if (wf?.id) {
        await updateWorkflow(wf.id, {
          configuration: {
            ...config,
            ...(templateId ? { template_id: templateId } : {}),
          },
          ...(templateId ? { templateId } : {}),
        });
      }

      navigate('/prioritization/settings/workflows', { state: { created: true } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function renderStep() {
    switch (STEPS[step].id) {
      case 'name': return <StepName state={state} update={enhancedUpdate} />;
      case 'session': return <StepSession state={state} update={update} />;
      case 'governance': return <StepGovernance state={state} update={update} />;
      case 'criteria': return <StepCriteria state={state} update={update} />;
      case 'scores': return <StepScoreHandling state={state} update={update} />;
      case 'defaults': return <StepDefaultCriteria state={state} update={update} />;
      case 'review': return <StepReview state={state} />;
      default: return null;
    }
  }

  return (
    <div className={wizardShell}>
      {/* Header */}
      <div className={`sticky top-0 z-20 ${wizardHeaderBar}`}>
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/prioritization/settings/workflows')}
            className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] transition-colors"
          >
            <ArrowLeft size={16} />
            {step > 0 ? 'Back' : 'Cancel'}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={[
                  'h-1.5 rounded-full transition-all',
                  i < step ? 'w-4 bg-primary-400' :
                  i === step ? 'w-6 bg-primary-600' :
                  'w-1.5 bg-[var(--hover-tint)] dark:bg-[var(--rule)]',
                ].join(' ')}
              />
            ))}
          </div>
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {step + 1} / {STEPS.length}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        {/* Step header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            {(() => { const Icon = currentStep.icon; return <Icon size={18} className="text-primary-500" />; })()}
            <h1 className="text-xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {currentStep.title}
            </h1>
          </div>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">{currentStep.subtitle}</p>
        </div>

        {/* Step content */}
        {renderStep()}

        {/* Error */}
        {error && (
          <div className="mt-4 border border-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 dark:border-[var(--coral)]/50 p-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
            {error}
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/prioritization/settings/workflows')}
            className={wizardBtnSecondary}
          >
            <ArrowLeft size={16} />
            {step > 0 ? 'Back' : 'Cancel'}
          </button>

          {isLast ? (
            <button
              onClick={handleCreate}
              disabled={saving || !state.name.trim()}
              className={`${wizardBtnPrimary} min-w-[140px]`}
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              {saving ? 'Creating…' : 'Create workflow'}
            </button>
          ) : (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canAdvance}
              className={`${wizardBtnPrimary} disabled:opacity-40`}
            >
              Continue
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
